import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatesValues } from '../../../services/celebration-dates.service';
import { validateBirth, validateHire } from '../../celebration-dates/celebration-dates.rules';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Campos de cumpleaños (día, mes y año opcional) y fecha de ingreso. Se usa en el aviso al iniciar sesión y en el
 * registro; avisa a quien lo usa de lo que se escribió y de si es válido.
 */
@Component({
  selector: 'app-celebration-dates-fields',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="cdf-group" *ngIf="showBirthday">
      <label class="cdf-label" for="cdf-day">🎂 Tu cumpleaños</label>
      <div class="cdf-row">
        <select id="cdf-day" class="cdf-field" [(ngModel)]="day" (ngModelChange)="changed()" aria-label="Día">
          <option [ngValue]="null">Día</option>
          <option *ngFor="let d of days" [ngValue]="d">{{ d }}</option>
        </select>
        <select class="cdf-field" [(ngModel)]="month" (ngModelChange)="changed()" aria-label="Mes">
          <option [ngValue]="null">Mes</option>
          <option *ngFor="let m of months; let i = index" [ngValue]="i + 1">{{ m }}</option>
        </select>
        <input class="cdf-field" type="number" inputmode="numeric" placeholder="Año" [(ngModel)]="year" (ngModelChange)="changed()" aria-label="Año de nacimiento" />
      </div>
      <p class="cdf-error" *ngIf="birthError" role="alert">{{ birthError }}</p>
      <p class="cdf-hint" *ngIf="!birthError">El año es opcional. Tu equipo lo verá solo como día y mes.</p>
    </div>

    <div class="cdf-group" *ngIf="showHire">
      <label class="cdf-label" for="cdf-hire">🎉 Tu aniversario en la empresa</label>
      <input id="cdf-hire" class="cdf-field" type="date" [(ngModel)]="hireDate" (ngModelChange)="changed()" aria-label="Fecha de ingreso a la empresa" />
      <p class="cdf-error" *ngIf="hireError" role="alert">{{ hireError }}</p>
      <p class="cdf-hint" *ngIf="!hireError">Es la fecha en que empezaste a trabajar en tu empresa.</p>
    </div>
  `,
  styles: [
    `
      :host { display: flex; flex-direction: column; gap: 12px; text-align: left; }
      .cdf-group { display: flex; flex-direction: column; gap: 6px; }
      .cdf-label { font-size: 0.8rem; font-weight: 800; color: #fff; }
      .cdf-row { display: grid; grid-template-columns: 1fr 1.4fr 1.4fr; gap: 8px; }
      .cdf-field { min-height: 44px; min-width: 0; padding: 8px 10px; border-radius: 14px; font-size: 0.85rem; font-family: inherit; color: #fff; background: #0b132b; border: 1px solid #2a3d78; }
      .cdf-field:focus { outline: none; border-color: #a6ff00; box-shadow: 0 0 0 3px rgba(166, 255, 0, 0.16); }
      .cdf-hint { margin: 0; font-size: 0.72rem; color: #94a3b8; }
      .cdf-error { margin: 0; font-size: 0.75rem; font-weight: 700; color: #fca5a5; }
    `
  ]
})
export class CelebrationDatesFieldsComponent implements OnInit {
  /** Valores con los que arranca (por ejemplo, al volver atrás en el registro). */
  @Input() initial?: Partial<DatesValues>;
  @Input() showBirthday = true;
  @Input() showHire = true;
  /** Lo que se escribe y si es válido (las fechas vacías cuentan como válidas: son opcionales). */
  @Output() valuesChange = new EventEmitter<{ values: DatesValues; valid: boolean }>();

  readonly days = Array.from({ length: 31 }, (_, i) => i + 1);
  readonly months = MONTHS;

  day: number | null = null;
  month: number | null = null;
  year: number | null = null;
  hireDate = '';

  ngOnInit(): void {
    this.day = this.initial?.day ?? null;
    this.month = this.initial?.month ?? null;
    this.year = this.initial?.year ?? null;
    this.hireDate = this.initial?.hireDate ?? '';
  }

  get birthError(): string | null {
    return this.showBirthday ? validateBirth(this.day, this.month, this.year || null) : null;
  }

  get hireError(): string | null {
    return this.showHire ? validateHire(this.hireDate || null) : null;
  }

  changed(): void {
    this.valuesChange.emit({
      values: { day: this.day, month: this.month, year: this.year || null, hireDate: this.hireDate },
      valid: !this.birthError && !this.hireError
    });
  }
}
